import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEye } from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import getErrorMessage from '../../helpers/getErrorMessage';
import { IMPERSONATE_MODE, buildImpersonateUrl, withPreviewMode } from '../../helpers/previewSdk';
import useFetch from '../../helpers/useFetch';

const FAILED_TITLE = 'Could not open your site as this customer';

const POPUP_BLOCKED = 'Your browser blocked the new window. Allow pop-ups for this page and click again.';

// Opens the merchant's own storefront as one of its customers, in a second tab.
// The session is minted here, on the operator's admin credentials, and handed to
// the SDK on that page in the url's fragment — the profile is named by the
// session and never by the url, so the page cannot be re-pointed at somebody
// else's profile by whoever is holding the shop's public token.
//
// Only the outbound half lives here: this tab stays where it is, so there is
// nothing to come back to and no session state to keep. The session's own expiry
// ends it.
//
// `userId` is the bare id, not the `profile/<domainId>/<userId>` form: the api
// builds the prefix itself from the operator's own session domain, so a profile
// on somebody else's domain cannot be named at all.
const ImpersonateProfile = ({ userId }) => {
  const { domain } = useSelector((state) => state.domain);
  const fetch = useFetch();
  const [starting, setStarting] = useState(false);

  // Where the session opens: the shop's home page, in the impersonation mode.
  // `null` when the domain has no url, or one that is not an address a tab can
  // be sent to — which is also what decides whether this control appears.
  const siteUrl = withPreviewMode(domain.url, IMPERSONATE_MODE);

  const start = async () => {
    // Opened before any await, or the popup blocker takes it — a window.open
    // that is not a direct consequence of the click is not user-initiated. It
    // sits on about:blank until the token exists.
    const shopWindow = window.open('', '_blank');

    if (!shopWindow) {
      notifications.show({ title: FAILED_TITLE, message: POPUP_BLOCKED, color: 'danger' });
      return;
    }

    // Disowned while it is still same-origin about:blank: once it is on the
    // shop's page, any script running there could otherwise navigate this tab
    // through `window.opener.location` ('noopener' isn't usable here, since the
    // handle is needed to set `.location` once the token exists).
    shopWindow.opener = null;

    setStarting(true);

    try {
      const { token } = await fetch(
        `/api/admin/accounts/${domain.accountId}/domains/${domain.id}/profiles/${encodeURIComponent(
          userId
        )}/impersonation-sessions`,
        { method: 'POST' }
      );

      // A 202/204, or a body that did not parse, resolves to `{}` in useFetch
      // rather than rejecting. Without a token there is no session to open, and
      // navigating anyway would show the shop as nobody with nothing said.
      if (!token) {
        throw new Error('The session came back empty.');
      }

      shopWindow.location = buildImpersonateUrl(siteUrl, token);
    } catch (e) {
      shopWindow.close();
      notifications.show({
        title: FAILED_TITLE,
        message: getErrorMessage(e, 'Could not start an impersonation session. Please try again or contact support.'),
        color: 'danger',
      });
    } finally {
      setStarting(false);
    }
  };

  if (!siteUrl) {
    return null;
  }

  return (
    <Button size="sm" variant="default" loading={starting} onClick={start} leftSection={<IconEye size={16} />}>
      Impersonate
    </Button>
  );
};

export default ImpersonateProfile;
